import React, { useEffect, useState, useMemo } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import Button from "../../../components/Button";
import DropField from "../../../components/DropField";

const API_URL =
  "https://api.eva-googlemarine.com/departamentos/tecnicos/produtos";

// --- Utils -----------------------------------------------------
// extrai apenas dígitos de uid e retorna como string (ou "")
function sanitizeuidToId(uid: unknown): string {
  const pic = String(uid ?? "");
  const onlyDigits = pic.match(/\d+/)?.[0] ?? "";
  return onlyDigits;
}

// aceita entradas como "1,25 kg", "1.25kg", "1250 g" e converte para kg (string com ponto)
function parseWeightToKg(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  const hasKg = /k\s*g/.test(s);
  const hasG = /(^|\s|\d)g(\s|$)/.test(s) && !hasKg;

  const numStr = s.replace(/[^0-9.,+-]/g, "").replace(/,/g, ".");
  const n = parseFloat(numStr);
  if (!isFinite(n) || n <= 0) return null;

  const kg = hasG ? n / 1000 : n;
  return Number(kg.toFixed(3)).toString();
}

// converte dd/MM/yyyy -> yyyy-MM-dd (ISO date-only)
function toISODate(br: string): string | null {
  if (!br) return null;
  const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
}

// === Dinheiro: normaliza para string "12345.67" (2 casas) --------------
function toApiMoney(raw: string): string {
  const s = String(raw ?? "").trim();
  if (!s) return "0.00";
  const clean = s.replace(/[^0-9.,-]/g, "");
  const lastComma = clean.lastIndexOf(",");
  const lastDot = clean.lastIndexOf(".");
  const decPos = Math.max(lastComma, lastDot);

  if (decPos === -1) {
    const intPart = clean.replace(/\D/g, "") || "0";
    return `${intPart}.00`;
  }

  const intPart = clean.slice(0, decPos).replace(/\D/g, "") || "0";
  let fracPart = clean.slice(decPos + 1).replace(/\D/g, "");

  if (fracPart.length > 2) {
    const n = Number(`${intPart}.${fracPart || "0"}`);
    return n.toFixed(2);
  }
  if (fracPart.length === 1) fracPart = fracPart + "0";
  if (fracPart.length === 0) fracPart = "00";

  return `${intPart}.${fracPart}`;
}
function isValidApiMoney(s: string): boolean {
  return /^-?\d+(\.\d{2})$/.test(s);
}

// ===== Moedas aceitas =====
const ALLOWED_CURRENCIES = ["BRL", "USD", "EUR"] as const;
type AllowedCurrency = (typeof ALLOWED_CURRENCIES)[number];

const RegistrarProduto: React.FC = () => {
  const [ProductCode, setProductCode] = useState("");
  const [ProductNCM, setProductNCM] = useState("");
  const [ProductName, setProductName] = useState("");
  const [ProductClassification, setProductClassification] = useState("");
  const [ProductCost, setProductCost] = useState("");
  const [ProductPosition, setProductPosition] = useState("");
  const [ProductDimension, setProductDimension] = useState("");
  const [ProductWeight, setProductWeight] = useState("");

  // >>> NOVO: moeda do custo
  const [ProductCurrency, setProductCurrency] =
    useState<AllowedCurrency>("BRL");

  const currencyLocale = useMemo(() => {
    switch (ProductCurrency) {
      case "USD":
        return "en-US"; // US$ 1,234.56
      case "EUR":
        return "pt-BR"; // ou "de-DE" se quiser padrão europeu
      case "BRL":
      default:
        return "pt-BR";
    }
  }, [ProductCurrency]);

  // mínimos obrigatórios do backend:
  const [InitialQuantity, setInitialQuantity] = useState(""); // inteiro
  const [ResponsibleId, setResponsibleId] = useState(""); // inteiro (preenchido via /auth/me)
  const [EmpresaId, setEmpresaId] = useState("");
  const [MovementDate, setMovementDate] = useState(""); // opcional (DD/MM/AAAA)

  // locks
  const [lockCost, setLockCost] = useState(true);
  const [lockDate, setLockDate] = useState(true);
  const [newCodeLoading, setNewCodeLoading] = useState(false);
  const [lockNCM, setLockNCM] = useState(true);
  const [lockDimension, setLockDimension] = useState(true);
  const [lockWeight, setLockWeight] = useState(true);
  const [lockName, setLockName] = useState(true);
  const [lockClassification, setLockClassification] = useState(true);
  const [lockQuantity, setLockQuantity] = useState(true);
  const [lockResponsible, setLockResponsible] = useState(true);
  const [lockPosition, setLockPosition] = useState(true);

  // flag para bloquear cadastro quando produto carregado está oculto
  const [isOculto, setIsOculto] = useState(false);

  // >>> BLOQUEIO POR DEPARTAMENTO
  const [isBlockedByDept, setIsBlockedByDept] = useState(false);
  const [blockedDeptName, setBlockedDeptName] = useState<string | null>(null);

  function unlockAllFields() {
    setLockNCM(false);
    setLockDimension(false);
    setLockWeight(false);
    setLockName(false);
    setLockClassification(false);
    setLockQuantity(false);
    setLockResponsible(false);
    setLockPosition(false);
    setLockCost(false);
    setLockDate(false);
  }
  function verifylockFields() {
    setLockNCM(true);
    setLockDimension(false);
    setLockWeight(false);
    setLockName(true);
    setLockClassification(true);
    setLockQuantity(false);
    setLockResponsible(true);
    setLockPosition(false);
    setLockCost(false);
    setLockDate(true);
  }
  function lockAllNonCodeFields() {
    setLockNCM(true);
    setLockDimension(true);
    setLockWeight(true);
    setLockName(true);
    setLockClassification(true);
    setLockQuantity(true);
    setLockResponsible(true);
    setLockPosition(true);
    setLockCost(true);
    setLockDate(true);
  }

  function clearAllFieldsButCode() {
    setProductNCM("");
    setProductName("");
    setProductClassification("");
    setProductCost("");
    setProductPosition("");
    setProductDimension("");
    setProductWeight("");
    setInitialQuantity("");
    setMovementDate("");
    setProductCurrency("BRL");
  }
  function unlockForManualRegistration() {
    setLockNCM(false);
    setLockDimension(false);
    setLockWeight(false);
    setLockName(false);
    setLockClassification(false);
    setLockQuantity(false);
    setLockResponsible(false);
    setLockPosition(false);
    setLockCost(false);
    setLockDate(false);
  }

  const opcoes = [
    { value: "A1", label: "A1" },
    { value: "A2", label: "A2" },
    { value: "A3", label: "A3" },
    { value: "A4", label: "A4" },
    { value: "A5", label: "A5" },
    { value: "A6", label: "A6" },
    { value: "B1", label: "B1" },
    { value: "B2", label: "B2" },
    { value: "B3", label: "B3" },
    { value: "B4", label: "B4" },
    { value: "B5", label: "B5" },
    { value: "B6", label: "B6" },
    { value: "C1", label: "C1" },
    { value: "C2", label: "C2" },
    { value: "C3", label: "C3" },
    { value: "C4", label: "C4" },
    { value: "C5", label: "C5" },
    { value: "C6", label: "C6" },
    { value: "D1", label: "D1" },
    { value: "D2", label: "D2" },
    { value: "D3", label: "D3" },
    { value: "D4", label: "D4" },
    { value: "D5", label: "D5" },
    { value: "D6", label: "D6" },
    { value: "E1", label: "E1" },
    { value: "E2", label: "E2" },
    { value: "E3", label: "E3" },
    { value: "E4", label: "E4" },
    { value: "E5", label: "E5" },
    { value: "E6", label: "E6" },
    { value: "F1", label: "F1" },
    { value: "F2", label: "F2" },
    { value: "F3", label: "F3" },
    { value: "F4", label: "F4" },
    { value: "F5", label: "F5" },
    { value: "F6", label: "F6" },
    { value: "G1", label: "G1" },
    { value: "G2", label: "G2" },
    { value: "G3", label: "G3" },
    { value: "G4", label: "G4" },
    { value: "G5", label: "G5" },
    { value: "G6", label: "G6" },
    { value: "H1", label: "H1" },
    { value: "H2", label: "H2" },
    { value: "H3", label: "H3" },
    { value: "H4", label: "H4" },
    { value: "H5", label: "H5" },
    { value: "H6", label: "H6" },
    { value: "I1", label: "I1" },
    { value: "I2", label: "I2" },
    { value: "I3", label: "I3" },
    { value: "I4", label: "I4" },
    { value: "I5", label: "I5" },
    { value: "I6", label: "I6" },
    { value: "J1", label: "J1" },
    { value: "J2", label: "J2" },
    { value: "J3", label: "J3" },
    { value: "J4", label: "J4" },
    { value: "J5", label: "J5" },
    { value: "J6", label: "J6" },
    { value: "CENTRAL 1", label: "CENTRAL 1" },
    { value: "CENTRAL 2", label: "CENTRAL 2" },
    { value: "CENTRAL 3", label: "CENTRAL 3" },
    { value: "CENTRAL 4", label: "CENTRAL 4" },
    { value: "CENTRAL 5", label: "CENTRAL 5" },
    { value: "CENTRAL 6", label: "CENTRAL 6" },
    { value: "CENTRAL 7", label: "CENTRAL 7" },
    { value: "CENTRAL 8", label: "CENTRAL 8" },
  ];
  const opcoesClassificacao = [
    { value: "ESTOQUE", label: "ESTOQUE" },
    { value: "CONSUMO", label: "CONSUMO" },
    { value: "RH", label: "RH" },
    { value: "VENDA", label: "VENDA" },
  ];
  // >>> NOVO: opções de moeda
  const opcoesMoeda = [
    { value: "BRL", label: "BRL" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
  ];

  const NoticeBanner: React.FC<{
    notice: { type: "success" | "error" | "notify"; text: string };
    onClose?: () => void;
    sticky?: boolean;
  }> = ({ notice, onClose, sticky = true }) => {
    let bg, color, border;
    switch (notice.type) {
      case "success":
        bg = "#e6ffed";
        color = "#0f5132";
        border = "#badbcc";
        break;
      case "error":
        bg = "#ffecec";
        color = "#842029";
        border = "#f5c2c7";
        break;
      case "notify":
      default:
        bg = "#e7f1ff";
        color = "#084298";
        border = "#b6d4fe";
        break;
    }

    return (
      <div
        role="alert"
        style={{
          margin: "12px 0",
          padding: "10px 12px",
          borderRadius: 8,
          background: bg,
          color: color,
          border: `1px solid ${border}`,
          position: sticky ? "sticky" : "static",
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          {onClose && (
            <button
              type="button"
              aria-label="Fechar aviso"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "inherit",
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  // feedback UI
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  // 1) Ao montar: buscar /auth/me e preencher ResponsibleId com ID de sessão
  useEffect(() => {
    fetch("https://api.eva-googlemarine.com/auth/me", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then((data) => {
        // ----- Responsável -----
        const rawResp = (data?.sessao?.id ??
          data?.sessao?.usuario_id ??
          sanitizeuidToId(data?.sessao?.uid)) as any;

        const asRespNum = Number(rawResp);
        if (Number.isFinite(asRespNum) && asRespNum > 0) {
          setResponsibleId(String(asRespNum));
        } else {
          setNotice({
            type: "notify",
            text: "Não foi possível obter seu ID de sessão. Verifique o login para registrar produtos.",
          });
        }

        // ----- Empresa -----
        const rawEmpresa = (data?.sessao?.empresa_id ??
          data?.sessao?.empresaId ??
          data?.sessao?.empresa ??
          "1") as any; // default 1 se não vier

        const asEmpresaNum = Number(rawEmpresa);
        if (Number.isFinite(asEmpresaNum) && asEmpresaNum > 0) {
          setEmpresaId(String(asEmpresaNum));
        } else {
        }
      })
      .catch(() => {
        setNotice({
          type: "notify",
          text: "Falha ao consultar /auth/me. Verifique sua sessão.",
        });
        // em caso de erro, manter EmpresaId vazio ou "1"? Aqui forço "1" como fallback
        setEmpresaId("1");
      });
  }, []);

  async function handleVerify() {
    setNotice(null);

    const codigo = ProductCode.trim();
    if (!codigo) {
      setNotice({
        type: "error",
        text: "Informe o código do produto para verificar.",
      });
      return;
    }

    try {
      const res = await fetch(
        `https://api.eva-googlemarine.com/departamentos/tecnicos/produtos/${encodeURIComponent(
          codigo
        )}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );

      const data = await res.json().catch(() => ({} as any));

      // >>> BLOQUEIO POR DEPARTAMENTO: trata erro específico
      if (
        data &&
        data.ok === false &&
        typeof data.erro === "string" &&
        data.erro.trim().toLowerCase() ===
          "esse produto possui registro em outro departamento" &&
        data.departamento
      ) {
        // bloqueia todos os campos (menos código) e botão Registrar
        lockAllNonCodeFields();
        setIsOculto(false);
        setIsBlockedByDept(true);
        setBlockedDeptName(String(data.departamento).toUpperCase());

        setNotice({
          type: "error",
          text: `Este código já está vinculado ao departamento: ${String(
            data.departamento
          ).toUpperCase()}. Altere o código e clique em "Verificar" para continuar.`,
        });
        return;
      }

      if (!res.ok || !data?.ok) {
        // não existe → fluxo de cadastro manual
        clearAllFieldsButCode();
        unlockForManualRegistration();
        setIsOculto(false);

        // >>> LIMPA BLOQUEIO DEPARTAMENTO (pois este código não existe no outro depto)
        setIsBlockedByDept(false);
        setBlockedDeptName(null);

        setNotice({
          type: "notify",
          text: "Código não registrado. Preencha os campos para registrar.",
        });
        return;
      }

      // Existe. Vamos preencher campos.
      const produto = data.data?.produto ?? {};
      const estoque = data.data?.estoque_atual ?? null;
      const custos: any[] = data.data?.custos ?? [];

      setProductNCM(produto.ncm ?? "");
      setProductName(produto.nome ?? "");
      setProductClassification(
        (produto.classificacao ?? "").toString().toUpperCase()
      );
      setProductPosition(produto.posicao ?? "");
      setProductWeight(produto.peso ?? "");
      setProductDimension(produto.dimensao ?? "");
      setInitialQuantity("");

      // Custo → mantém visual com vírgula:
      let custo = "";
      if (estoque?.custo_unitario_atual)
        custo = String(estoque.custo_unitario_atual);
      else if (custos.length && custos[0]?.custo_unitario)
        custo = String(custos[0].custo_unitario);
      if (custo) setProductCost(custo.replace(".", ",")); // visual

      // >>> NOVO: detectar moeda do backend, se existir
      const moedaDetectada =
        (estoque && (estoque.moeda as any)) ||
        (produto && (produto.moeda as any)) ||
        null;
      if (
        moedaDetectada &&
        ALLOWED_CURRENCIES.includes(String(moedaDetectada) as AllowedCurrency)
      ) {
        setProductCurrency(moedaDetectada as AllowedCurrency);
      } else {
        setProductCurrency("BRL");
      }

      // Data de hoje no formato DD/MM/AAAA (UI)
      const hoje = new Date();
      const dia = String(hoje.getDate()).padStart(2, "0");
      const mes = String(hoje.getMonth() + 1).padStart(2, "0");
      const ano = hoje.getFullYear();
      setMovementDate(`${dia}/${mes}/${ano}`);

      // Oculto?
      const oculto = Boolean(data?.oculto ?? data?.data?.produto?.ocultar);
      setIsOculto(oculto);

      // >>> LIMPA BLOQUEIO DEPARTAMENTO (pois pertence ao RH)
      setIsBlockedByDept(false);
      setBlockedDeptName(null);

      if (oculto) {
        lockAllNonCodeFields();
        setNotice({
          type: "notify",
          text: "Este código de produto se encontra como oculto. Modifique para poder utilizá-lo.",
        });
        return;
      }

      verifylockFields();
      setNotice({
        type: "success",
        text: `Produto encontrado, carregando dados para ${
          produto.codigo ?? codigo
        }.`,
      });
    } catch (e: any) {
      setNotice({
        type: "error",
        text: `Falha na verificação: ${e?.message || "erro de rede."}`,
      });
    }
  }

  async function handleRegister() {
    setNotice(null);

    if (isOculto) {
      setNotice({
        type: "error",
        text: "Este código está oculto. Digite um outro código e verifique para prosseguir com o registro.",
      });
      return;
    }

    // >>> BLOQUEIO POR DEPARTAMENTO: impede registro
    if (isBlockedByDept) {
      setNotice({
        type: "error",
        text:
          `Este código está bloqueado por já existir no departamento ${
            blockedDeptName ?? ""
          }. ` + `Altere o código e clique em "Verificar".`,
      });
      return;
    }

    if (!ProductCode.trim()) {
      setNotice({ type: "error", text: "Informe o código do produto." });
      return;
    }
    if (!ProductName.trim()) {
      setNotice({ type: "error", text: "Informe o nome do produto." });
      return;
    }
    if (!ProductCost.trim()) {
      setNotice({ type: "error", text: "Informe o custo unitário." });
      return;
    }
    if (!InitialQuantity.trim() || isNaN(Number(InitialQuantity))) {
      setNotice({
        type: "error",
        text: "Erro: a quantidade adicionada não pode ser < 1.",
      });
      return;
    }

    const qty = Number(InitialQuantity);
    if (qty <= 0) {
      setNotice({
        type: "error",
        text: "Para registrar o produto, preencha a Quantidade com um número inteiro maior que 0.",
      });
      return;
    }

    if (!ResponsibleId.trim() || isNaN(Number(ResponsibleId))) {
      setNotice({
        type: "error",
        text: "Responsável (ID) deve ser um número inteiro.",
      });
      return;
    }

    // ✅ validar moeda
    if (!ProductCurrency || !ALLOWED_CURRENCIES.includes(ProductCurrency)) {
      setNotice({
        type: "error",
        text: "Selecione uma moeda válida (BRL, USD ou EUR).",
      });
      return;
    }

    const custoFormatado = toApiMoney(ProductCost);
    if (!isValidApiMoney(custoFormatado)) {
      setNotice({ type: "error", text: "Custo unitário inválido." });
      return;
    }

    let pesoFormatado: string | null = null;
    if (ProductWeight && ProductWeight.trim()) {
      const parsed = parseWeightToKg(ProductWeight);
      if (!parsed) {
        setNotice({
          type: "error",
          text: "Peso inválido. Use um número (ex.: 1,25 kg ou 1250 g).",
        });
        return;
      }
      pesoFormatado = parsed; // já em kg
    }

    const isoDate = MovementDate ? toISODate(MovementDate) : null;
    if (MovementDate && !isoDate) {
      setNotice({
        type: "error",
        text: "Data inválida. Use o formato DD/MM/AAAA.",
      });
      return;
    }

    const payload: any = {
      codigo: ProductCode || null,
      nome: ProductName || null,
      ncm: ProductNCM || null,
      classificacao: ProductClassification || null,
      posicao: ProductPosition || null,
      dimensao: ProductDimension || null,
      peso: pesoFormatado,
      // obrigatórios para o movimento inicial:
      custo_unitario: custoFormatado, // "1234.56"
      moeda: ProductCurrency, // <<< ENVIA A MOEDA
      quantidade: qty,
      responsavel_registro: Number(ResponsibleId),
      empresa_id: Number(EmpresaId),
      // data_movimentacao: isoDate ?? undefined,
    };

    setSubmitting(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok === false) {
        const backendMsg =
          data?.erro ||
          (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
          data?.message ||
          "Falha ao registrar.";
        setNotice({ type: "error", text: backendMsg });
        return;
      }

      setNotice({ type: "success", text: "Produto registrado com sucesso!" });

      // limpar campos após sucesso (NÃO limpar ResponsibleId)
      setProductCode("");
      setProductNCM("");
      setProductName("");
      setProductClassification("");
      setProductCost("");
      setProductPosition("");
      setProductDimension("");
      setProductWeight("");
      setInitialQuantity("");
      setMovementDate("");
      setIsOculto(false);
      setIsBlockedByDept(false);
      setBlockedDeptName(null);
      setProductCurrency("BRL");
      unlockAllFields();
    } catch (err: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${err?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <TitleButtons
        title="Laboratório - Registrar Produto"
        /*buttons={[
          {
            label: newCodeLoading ? "Gerando..." : "Gerar novo código",
            onClick: handleNewCode,
            variant: "secondary",
          },
        ]}*/
      />

      {(notice?.type === "success" || notice?.type === "notify") && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={true}
        />
      )}
      {notice?.type === "error" && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={false}
        />
      )}

      <div>
        <TextField
          label="Código do Produto"
          value={ProductCode}
          onChange={setProductCode}
          placeholder="Digite aqui o código do Produto"
          uppercase={true}
          button
          buttonText="Verificar"
          onButtonClick={handleVerify}
          // Campo de código SEM lock mesmo quando bloqueado por depto
        />

        <TextField
          label="NCM"
          value={ProductNCM}
          onChange={setProductNCM}
          placeholder="Digite aqui o NCM do Produto"
          locked={lockNCM || isBlockedByDept} // >>> BLOQUEIO POR DEPARTAMENTO
        />

        <TextField
          label="Dimensões"
          value={ProductDimension}
          onChange={setProductDimension}
          placeholder="Ex.: 10x20x30cm"
          locked={lockDimension || isBlockedByDept}
        />

        <TextField
          label="Peso"
          value={ProductWeight}
          onChange={setProductWeight}
          placeholder="Ex.: 1,25 kg ou 1250 g"
          locked={lockWeight || isBlockedByDept}
        />

        <TextField
          label="Nome do Produto"
          value={ProductName}
          onChange={setProductName}
          placeholder="Digite aqui o Nome do Produto"
          uppercase={true}
          locked={lockName || isBlockedByDept}
        />

        <DropField
          label="Classificação"
          value={ProductClassification}
          onChange={(v) => setProductClassification(v ?? "")}
          options={opcoesClassificacao}
          placeholder="Selecione..."
          uppercase
          locked={lockClassification || isBlockedByDept}
        />

        {/* ===== Custo Unitário + Moeda (lado a lado) ===== */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 140px",
            gap: 8,
            alignItems: "end",
          }}
        >
          <TextField
            label="Custo Unitário"
            value={ProductCost}
            onChange={setProductCost}
            placeholder="Ex.: 12,50"
            contabil
            currency={ProductCurrency}
            currencyLocale={currencyLocale}
            locked={lockCost || isBlockedByDept}
          />

          <DropField
            label="Moeda"
            value={ProductCurrency}
            onChange={(v) =>
              setProductCurrency((v as string as AllowedCurrency) ?? "BRL")
            }
            options={opcoesMoeda}
            placeholder="Selecione..."
            uppercase
            locked={lockCost || isBlockedByDept}
          />
        </div>

        <TextField
          label="Quantidade"
          value={InitialQuantity}
          onChange={setInitialQuantity}
          placeholder="Insira aqui a quantidade a ser adicionada, Ex: 10"
          locked={lockQuantity || isBlockedByDept}
        />

        <DropField
          label="Posição no Estoque"
          value={ProductPosition}
          onChange={(v) => setProductPosition(v ?? "")}
          options={opcoes}
          placeholder="Selecione..."
          uppercase
          locked={lockPosition || isBlockedByDept}
        />
      </div>

      <Button
        text={submitting ? "Registrando..." : "Registrar"}
        onClick={handleRegister}
        disabled={
          submitting || !ResponsibleId || isOculto || isBlockedByDept // >>> BLOQUEIO POR DEPARTAMENTO
        }
      />
    </div>
  );
};

export default RegistrarProduto;
